import type {
  AttributeDef,
  CategoryDef,
  EnumFacet,
  FacetOption,
  FacetSelection,
  NumberFacet,
  Product,
  VisibleFacet,
} from '../types';
import { sortCategories } from './sortCategories';

interface FacetConfig {
  key: string;
  label?: string;
  type: 'enum' | 'number';
  unit?: string;
  values: (p: Product) => string[];
}

/** Categoría (fija, desde tags) + una faceta por def filtrable del registro. */
const buildConfigs = (defs: AttributeDef[]): FacetConfig[] => [
  { key: 'categories', type: 'enum', values: p => (p.tags ? [p.tags] : []) },
  ...defs.reduce<FacetConfig[]>((acc, d) => {
    if (d.filterable) {
      acc.push({
        key: d.code,
        label: d.label,
        type: d.type as 'enum' | 'number',
        unit: d.unit,
        values: (p: Product) => p.attributes?.[d.code] ?? [],
      });
    }
    return acc;
  }, []),
];

export const getFacetKeys = (defs: AttributeDef[]): string[] => buildConfigs(defs).map(c => c.key);

const inRange = (cfg: FacetConfig, p: Product, active: string[]): boolean => {
  const [minStr, maxStr] = active;
  const min = Number(minStr);
  const max = Number(maxStr);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return true;
  const raw = cfg.values(p)[0];
  if (raw === undefined) return false;
  const value = Number(raw);
  return value >= min && value <= max;
};

export function applyFacets(
  all: Product[],
  selection: FacetSelection,
  defs: AttributeDef[],
): Product[] {
  const configs = buildConfigs(defs);
  return all.filter(p =>
    configs.every(cfg => {
      const active = selection[cfg.key] ?? [];
      if (active.length === 0) return true;
      if (cfg.type === 'number') return active.length === 2 ? inRange(cfg, p, active) : true;
      const productValues = cfg.values(p);
      return active.some(v => productValues.includes(v));
    }),
  );
}

export function computeFacets(
  all: Product[],
  selection: FacetSelection,
  defs: AttributeDef[],
  categoryDefs: CategoryDef[] = [],
): VisibleFacet[] {
  const configs = buildConfigs(defs);
  return configs
    .map((cfg): VisibleFacet | undefined => {
      const othersSelection: FacetSelection = { ...selection, [cfg.key]: [] };
      const scoped = applyFacets(all, othersSelection, defs);

      if (cfg.type === 'number') {
        const numbers = scoped
          .map(p => cfg.values(p)[0])
          .filter((v): v is string => v !== undefined)
          .map(Number);
        if (numbers.length < 2) return undefined;
        const min = Math.min(...numbers);
        const max = Math.max(...numbers);
        if (min === max) return undefined;
        const facet: NumberFacet = {
          key: cfg.key,
          label: cfg.label,
          type: 'number',
          unit: cfg.unit,
          min,
          max,
        };
        return facet;
      }

      const counts = new Map<string, number>();
      scoped.forEach(p => cfg.values(p).forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1)));
      const values = [...counts.keys()];
      const alphabetical = values.toSorted((a, b) => (a < b ? -1 : 1));
      const orderedValues =
        cfg.key === 'categories' ? sortCategories(alphabetical, categoryDefs) : alphabetical;
      const options: FacetOption[] = orderedValues.map(value => ({
        value,
        count: counts.get(value)!,
      }));
      if (options.length < 2) return undefined;
      const facet: EnumFacet = { key: cfg.key, label: cfg.label, type: 'enum', options };
      return facet;
    })
    .filter((f): f is VisibleFacet => f !== undefined);
}
